import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { BRAND } from '../constants';
import { Anchor, Mail, Send, Instagram, Youtube, Linkedin, ArrowRight, Shield, Zap, Package, Map, CheckCircle2, AlertCircle, Loader2, UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';

export default function Footer() {
  const { content } = useBrand();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setStatus('error');
      setMessage('Email is required.');
      return;
    }
    if (!validateEmail(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    
    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) throw new Error('Subscription failed');

      setStatus('success');
      setMessage('Welcome to the Syndicate. Check your inbox.');
      setEmail('');
    } catch (error) {
      console.error('Newsletter error:', error);
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <footer className="bg-baja-sand py-24 border-t border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 mb-24">
          {/* Lead Magnet */}
          <div className="max-w-md">
            <div className="flex items-center gap-2 text-safety-orange mb-4">
              <Map className="h-5 w-5" />
              <span className="text-xs font-bold tracking-[0.3em] uppercase">Join the Syndicate</span>
            </div>
            <h2 className="text-2xl font-display font-light tracking-widest uppercase mb-6 leading-none text-black">
              JOIN THE SYNDICATE <br />
              <span className="text-black/40">FOR EXCLUSIVE UPDATES.</span>
            </h2>
            <p className="text-black/60 mb-8 font-light tracking-widest leading-relaxed">
              Join the Syndicate for exclusive technical updates and Batch 001 inventory alerts.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex gap-2">
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status !== 'idle') setStatus('idle');
                  }}
                  placeholder="EMAIL ADDRESS" 
                  disabled={status === 'loading'}
                  className="flex-1 bg-white border border-black/10 px-4 py-4 rounded-sm text-xs font-mono font-bold tracking-widest focus:outline-none focus:border-safety-orange transition-colors text-black disabled:opacity-50"
                />
                <button 
                  type="submit"
                  disabled={status === 'loading'}
                  className="bg-safety-orange text-white px-6 py-4 rounded-sm font-bold hover:bg-orange-600 transition-all flex items-center justify-center disabled:opacity-50"
                >
                  {status === 'loading' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
              
              <AnimatePresence mode="wait">
                {status === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-2 text-green-600 text-[10px] font-bold uppercase tracking-widest"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {message}
                  </motion.div>
                )}
                {status === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-2 text-red-500 text-[10px] font-bold uppercase tracking-widest"
                  >
                    <AlertCircle className="h-4 w-4" />
                    {message}
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
            <p className="mt-4 text-[10px] text-black/20 font-bold tracking-widest uppercase">
              No spam. Just engineering updates and expedition logs.
            </p>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-[10px] font-bold tracking-widest uppercase text-black mb-6">Ecosystem</h3>
              <ul className="space-y-4">
                <li><Link to="/syndicate" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Membership</Link></li>
                <li><Link to="/coaching" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Book Your Training</Link></li>
                <li><Link to="/expeditions" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Expeditions</Link></li>
                <li><Link to="/team-building" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Corporate</Link></li>
                <li><Link to="/adventures" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Mission Logs</Link></li>
                <li><Link to="/expedition-cx2" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Shop Gear</Link></li>
                <li><Link to="/contact" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Contact</Link></li>
                <li><a href="/#ows-faq-section" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Squad FAQ Intel</a></li>
                <li>
                  <Link 
                    to="/member-portal"
                    className="text-[10px] font-bold tracking-[0.2em] text-ows-teal hover:text-ows-dark transition-colors uppercase text-left mt-6 flex items-center gap-2"
                  >
                    <UserIcon className="h-3 w-3" /> Member Portal
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/admin-login"
                    className="text-[10px] font-bold tracking-[0.2em] text-ows-teal hover:text-ows-dark transition-colors uppercase text-left mt-2 flex items-center gap-2"
                  >
                    <Shield className="h-3 w-3" /> Admin Portal
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-[10px] font-bold tracking-widest uppercase text-black mb-6">Services</h3>
              <ul className="space-y-4">
                <li><Link to="/coaching" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">SUP Coaching</Link></li>
                <li><Link to="/team-building" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Team Building</Link></li>
                <li><Link to="/syndicate" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">The Syndicate</Link></li>
                <li><Link to="/gift" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">Gift Cards</Link></li>
                <li><Link to="/adventures" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase">The Log</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-[10px] font-bold tracking-widest uppercase text-black mb-6">Social</h3>
              <ul className="space-y-4">
                <li><a href="https://instagram.com/openwater.syndicate" target="_blank" rel="noopener noreferrer" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase flex items-center gap-2"><Instagram className="h-4 w-4" /> Instagram</a></li>
                <li><a href="https://www.youtube.com/@OWS-Canada" target="_blank" rel="noopener noreferrer" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase flex items-center gap-2"><Youtube className="h-4 w-4" /> Youtube</a></li>
                <li><a href="https://linkedin.com/in/open-water-syndicate-8b560a390" target="_blank" rel="noopener noreferrer" className="text-sm font-light tracking-widest text-black/40 hover:text-safety-orange transition-colors uppercase flex items-center gap-2"><Linkedin className="h-4 w-4" /> Linkedin</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between border-t border-black/5 pt-12 gap-8">
          <div className="flex items-center space-x-2">
            <Anchor className="h-6 w-6 text-deep-sea-teal" />
            <span className="font-display font-light text-lg tracking-widest uppercase text-black">{content.brandName}</span>
          </div>
          
          <div className="text-center">
            <p className="text-[10px] text-black/20 font-bold tracking-widest uppercase mb-1">
              © {new Date().getFullYear()} {content.brandName}. All Rights Reserved.
            </p>
            <p className="flex items-center justify-center gap-3 text-[9px] font-bold tracking-widest uppercase text-black/40">
              <Link to="/privacy-policy" className="hover:text-safety-orange transition-colors">Privacy Policy</Link>
              <span>•</span>
              <Link to="/terms-of-use" className="hover:text-safety-orange transition-colors">Terms of Use</Link>
              <span>•</span>
              <Link to="/full-waiver" className="hover:text-safety-orange transition-colors">Liability Waiver</Link>
              <span>•</span>
              <a href="/#ows-faq-section" className="hover:text-safety-orange transition-colors">Squad FAQ</a>
            </p>
          </div>

          <div className="flex items-center gap-4">
            <span className="h-2 w-2 bg-safety-orange rounded-full animate-pulse"></span>
            <span className="text-[10px] font-bold tracking-widest uppercase text-safety-orange">Batch 001: Active</span>
          </div>
        </div>
      </div>
      
      {/* Bottom requirement banner */}
      <a href="https://cert.openwatersyndicate.com" target="_blank" rel="noopener noreferrer" className="block bg-ows-gold text-white text-[10px] sm:text-[11px] font-mono font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-center py-4 px-4 hover:bg-ows-orange transition-colors mt-12 w-full">
        GET OWS CLEARANCE CERTIFICATION <span className="underline ml-4">CERT.OPENWATERSYNDICATE.COM</span>
        <span className="block mt-2 text-[9px] opacity-80">(REQUIREMENT TO ADVANCE TO ELITE STATUS)</span>
      </a>
    </footer>
  );
}
